/** SUNTRONIX ONE — Public (customer-safe) catalogue page for a shared
 *  Collection. No auth. Reached at /c/:token. */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Package, CheckCircle2, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

const fmtINR = (n) =>
  n ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n)
    : "Price on request";

export default function CataloguePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [errMsg, setErrMsg] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await pub.get(`/public/catalogue/${token}`);
      setData(data); setState("ready");
    } catch (e) {
      setErrMsg(e.response?.data?.detail || "This catalogue link is not available.");
      setState("error");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6" data-testid="catalogue-public-error">
        <div className="text-center max-w-md">
          <div className="h-14 w-14 rounded-full bg-slate-200 grid place-items-center mx-auto mb-4">
            <Package className="h-6 w-6 text-slate-500" />
          </div>
          <div className="text-lg font-semibold text-slate-900">Catalogue unavailable</div>
          <p className="text-[13px] text-slate-500 mt-2">{errMsg}</p>
        </div>
      </div>
    );
  }

  const brand = data.brand || {};
  return (
    <div className="min-h-screen bg-slate-50" data-testid="catalogue-public-page">
      {/* Branded header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="text-[15px] font-heading font-bold tracking-[0.18em]">
            {(brand.company_name || "SUNTRONIX").toUpperCase()}
          </div>
          {brand.website && (
            <a href={brand.website} target="_blank" rel="noreferrer"
              className="text-[12px] text-slate-300 hover:text-white">{brand.website}</a>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Curated selection</div>
            <h1 className="text-3xl font-heading font-semibold text-slate-900 mt-1">{data.title}</h1>
            {data.description && <p className="text-[14px] text-slate-500 mt-2 max-w-2xl">{data.description}</p>}
            <div className="text-[12px] text-slate-400 mt-2">{data.item_count} product{data.item_count === 1 ? "" : "s"}</div>
          </div>
          <button onClick={() => setFormOpen(true)}
            className="h-11 px-6 rounded-full bg-slate-900 text-white text-[14px] font-medium hover:bg-slate-800 transition-colors"
            data-testid="catalogue-public-cta">
            {data.cta_label || "Request a Quote"}
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.items.map((p, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden"
              data-testid={`catalogue-public-item-${i}`}>
              <div className="h-40 bg-slate-100 grid place-items-center overflow-hidden">
                {p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-contain" />
                  : <Package className="h-10 w-10 text-slate-300" />}
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400">{p.brand_code} · {p.family || "—"}</div>
                <div className="text-[15px] font-semibold text-slate-900 mt-1">{p.name}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">{p.model || p.part_number || ""}</div>
                {p.specs && Object.keys(p.specs).length > 0 && (
                  <div className="mt-3 space-y-0.5">
                    {Object.entries(p.specs).slice(0, 3).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-[11px]">
                        <span className="text-slate-400 uppercase text-[9px] tracking-wider">{k.replace(/_/g, " ")}</span>
                        <span className="text-slate-700 ml-2 truncate">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t border-slate-100 mt-3 pt-3 flex items-end justify-between">
                  {data.show_prices ? (
                    <div className="text-[15px] font-heading font-semibold text-slate-900">{fmtINR(p.list_price_inr)}</div>
                  ) : (
                    <div className="text-[12px] text-slate-500">Price on request</div>
                  )}
                  <div className="text-[10px] text-emerald-700 uppercase font-semibold">
                    {(p.availability || "").replace(/_/g, " ")}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <button onClick={() => setFormOpen(true)}
            className="h-11 px-6 rounded-full bg-slate-900 text-white text-[14px] font-medium hover:bg-slate-800"
            data-testid="catalogue-public-cta-bottom">
            {data.cta_label || "Request a Quote"}
          </button>
        </div>

        <div className="mt-10 text-center text-[11px] text-slate-400">
          {brand.company_name || "SUNTRONIX"}
          {brand.email ? ` · ${brand.email}` : ""}{brand.phone ? ` · ${brand.phone}` : ""}
        </div>
      </div>

      {formOpen && (
        <EnquiryForm token={token} cta={data.cta_label} onClose={() => setFormOpen(false)} />
      )}
    </div>
  );
}


function EnquiryForm({ token, cta, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await pub.post(`/public/catalogue/${token}/enquiry`, form);
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="catalogue-enquiry-form">
      <div className="bg-white rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6" data-testid="catalogue-enquiry-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <div className="text-lg font-semibold text-slate-900">Thank you!</div>
            <p className="text-[13px] text-slate-500 mt-2">Our team will be in touch shortly.</p>
            <button onClick={onClose} className="mt-5 h-10 px-6 rounded-full bg-slate-900 text-white text-[14px] hover:bg-slate-800"
              data-testid="catalogue-enquiry-close">Close</button>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-heading font-semibold text-slate-900">{cta || "Request a Quote"}</h2>
            <p className="text-[12px] text-slate-500 mt-1">Share your details and we'll get back to you.</p>
            <div className="mt-4 space-y-3">
              <input value={form.website} onChange={(e) => upd("website", e.target.value)}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <input value={form.name} onChange={(e) => upd("name", e.target.value)}
                placeholder="Your name *" className="w-full h-10 border border-slate-200 rounded-md px-3 text-[14px]"
                data-testid="enquiry-name" />
              <input value={form.email} onChange={(e) => upd("email", e.target.value)}
                placeholder="Email *" className="w-full h-10 border border-slate-200 rounded-md px-3 text-[14px]"
                data-testid="enquiry-email" />
              <input value={form.phone} onChange={(e) => upd("phone", e.target.value)}
                placeholder="Phone" className="w-full h-10 border border-slate-200 rounded-md px-3 text-[14px]"
                data-testid="enquiry-phone" />
              <input value={form.company} onChange={(e) => upd("company", e.target.value)}
                placeholder="Company" className="w-full h-10 border border-slate-200 rounded-md px-3 text-[14px]"
                data-testid="enquiry-company" />
              <textarea value={form.message} onChange={(e) => upd("message", e.target.value)} rows={3}
                placeholder="Message (optional)" className="w-full border border-slate-200 rounded-md p-3 text-[14px]"
                data-testid="enquiry-message" />
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={onClose} className="flex-1 h-10 rounded-md border border-slate-200 text-[14px] text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={submit} disabled={busy}
                className="flex-1 h-10 rounded-md bg-slate-900 text-white text-[14px] hover:bg-slate-800 inline-flex items-center justify-center gap-1.5"
                data-testid="enquiry-submit">
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
